'use client';

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Image as ImageIcon, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

import ProductImageCropDialog from '@/components/pages/dialogs/ProductImageCropDialog';
import { GenerateDescriptionDialog } from '@/components/pages/dialogs/GenerateDescriptionDialog';
import { useIA } from '@/components/hooks/useIA';
import type {
  Producto as ProductoDB,
  ProductoInput,
} from '@/components/hooks/useProductos';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productoToEdit: ProductoDB | null;
  onSave: () => void | Promise<void>;
  createProducto: (
    input: ProductoInput
  ) => Promise<{ ok: boolean; id_producto?: string | null }>;
  updateProducto: (
    id: string,
    patch: Partial<ProductoInput>
  ) => Promise<{ ok: boolean }>;
  uploadImagenProducto: (id: string, file: File) => Promise<{ ok: boolean }>;
  removeImagenProducto: (id: string) => Promise<{ ok: boolean }>;
};

const Field = ({
  label,
  children,
  extra,
}: {
  label: string;
  children: React.ReactNode;
  extra?: React.ReactNode;
}) => (
  <div className="space-y-1">
    <div className="flex items-center justify-between">
      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
        {label}
      </label>
      {extra}
    </div>
    {children}
  </div>
);

export default function AddProductDialog({
  open,
  onOpenChange,
  productoToEdit,
  onSave,
  createProducto,
  updateProducto,
  uploadImagenProducto,
  removeImagenProducto,
}: Props) {
  const { t } = useTranslation();
  const modoEdicion = Boolean(productoToEdit);

  const [saving, setSaving] = useState(false);
  const [nombre, setNombre] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [precio, setPrecio] = useState('');
  const [activo, setActivo] = useState(true);
  const [imagenPreview, setImagenPreview] = useState<string | null>(null);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);
  const [openCrop, setOpenCrop] = useState(false);
  const [croppedFile, setCroppedFile] = useState<File | null>(null);

  const {
    generarDescripcion,
    generarSiguiente,
    resetTono,
    loading: loadingIA,
  } = useIA();
  const [showIADialog, setShowIADialog] = useState(false);
  const [iaDescripcion, setIaDescripcion] = useState('');

  const handleGenerarDescripcion = async () => {
    if (!nombre) return;
    resetTono();
    const resultado = await generarDescripcion(
      'descripcion_servicio_simple' as any,
      { nombre_servicio: nombre, precio_servicio: precio || undefined } as any
    );
    setIaDescripcion(resultado);
    setShowIADialog(true);
  };

  const handleSiguienteVariante = async () => {
    if (!nombre) return;
    const resultado = await generarSiguiente(
      'descripcion_servicio_simple' as any,
      { nombre_servicio: nombre, precio_servicio: precio || undefined } as any
    );
    setIaDescripcion(resultado);
  };

  const titulo = useMemo(
    () =>
      modoEdicion
        ? t('addProductDialog.titleEdit')
        : t('addProductDialog.titleAdd'),
    [modoEdicion, t]
  );

  useEffect(() => {
    if (!open) return;

    if (productoToEdit) {
      setNombre(productoToEdit.nombre);
      setDescripcion(productoToEdit.descripcion ?? '');
      setPrecio(
        productoToEdit.precio != null ? String(productoToEdit.precio) : ''
      );
      setActivo(Boolean(productoToEdit.activo));
      setImagenPreview(
        productoToEdit.imagen_principal
          ? `${productoToEdit.imagen_principal}?t=${Date.now()}`
          : null
      );
      setCroppedFile(null);
      setFileToCrop(null);
      setOpenCrop(false);
      return;
    }

    resetForm();
  }, [open, productoToEdit]);

  const resetForm = () => {
    setNombre('');
    setDescripcion('');
    setPrecio('');
    setActivo(true);
    setImagenPreview(null);
    setCroppedFile(null);
    setFileToCrop(null);
    setOpenCrop(false);
  };

  const closeDialog = () => {
    onOpenChange(false);
    resetForm();
  };

  const handlePickFile = (file: File | null) => {
    if (!file) return;
    setFileToCrop(file);
    setOpenCrop(true);
  };

  const handleCroppedSave = (file: File) => {
    setCroppedFile(file);
    setImagenPreview(URL.createObjectURL(file));
    setOpenCrop(false);
    setFileToCrop(null);
  };

  const handleRemoveImage = async () => {
    if (!imagenPreview) return;

    if (!modoEdicion) {
      setImagenPreview(null);
      setCroppedFile(null);
      return;
    }

    if (!productoToEdit?.id_producto) {
      toast.error(t('addProductDialog.errorImageRemove'));
      return;
    }

    setSaving(true);
    const { ok } = await removeImagenProducto(productoToEdit.id_producto);
    setSaving(false);

    if (ok) {
      setImagenPreview(null);
      setCroppedFile(null);
      await onSave();
    }
  };

  const parsePrecio = (v: string): number | null => {
    if (!v.trim()) return null;
    const n = Number(v);
    return Number.isNaN(n) || n < 0 ? null : n;
  };

  const handleSave = async () => {
    const precioNum = parsePrecio(precio);
    setSaving(true);

    try {
      let idProducto = productoToEdit?.id_producto;

      if (modoEdicion && idProducto) {
        const { ok } = await updateProducto(idProducto, {
          nombre: nombre.trim(),
          descripcion: descripcion.trim() || null,
          precio: precioNum,
          activo,
        });
        if (!ok) return;
      } else {
        const result = await createProducto({
          nombre: nombre.trim(),
          descripcion: descripcion.trim() || null,
          precio: precioNum,
          activo,
        });
        if (!result.ok || !result.id_producto) return;
        idProducto = result.id_producto;
      }

      if (croppedFile && idProducto) {
        const { ok } = await uploadImagenProducto(idProducto, croppedFile);
        if (!ok) return;
      }

      toast.success(
        modoEdicion
          ? t('addProductDialog.successUpdate')
          : t('addProductDialog.successCreate')
      );
      await onSave();
      closeDialog();
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open && !showIADialog && !openCrop} onOpenChange={onOpenChange}>
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none max-h-[90vh] rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[90vh] sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
                {titulo}
              </DialogTitle>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {modoEdicion
                  ? t('addProductDialog.descEdit')
                  : t('addProductDialog.descAdd')}
              </p>
            </DialogHeader>
            <button
              onClick={closeDialog}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-4">
              {/* Imagen */}
              <Field label={t('addProductDialog.imageLabel')}>
                <div className="relative">
                  <label className="flex items-center justify-center border-2 border-dashed border-gray-300 dark:border-white/10 rounded-xl h-36 cursor-pointer hover:bg-gray-50 dark:hover:bg-white/10 transition-colors overflow-hidden bg-gray-100 dark:bg-white/5">
                    {imagenPreview ? (
                      <img
                        src={imagenPreview}
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <div className="flex flex-col items-center text-gray-400 dark:text-gray-500">
                        <ImageIcon className="w-8 h-8 mb-1" />
                        <span className="text-xs">
                          {t('addProductDialog.imageUpload')}
                        </span>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) =>
                        handlePickFile(e.target.files?.[0] ?? null)
                      }
                    />
                  </label>
                  {imagenPreview && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 bg-white dark:bg-[#1c1c1e] rounded-full p-1 shadow hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                    >
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  )}
                </div>
              </Field>

              {/* Nombre */}
              <Field label={t('addProductDialog.nameLabel')}>
                <Input
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  placeholder={t('addProductDialog.namePlaceholder')}
                  className="h-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </Field>

              {/* Descripción */}
              <Field
                label={t('addProductDialog.descLabel')}
                extra={
                  nombre ? (
                    <button
                      type="button"
                      onClick={handleGenerarDescripcion}
                      disabled={loadingIA}
                      className="flex items-center gap-1.5 text-xs font-medium text-[#103254] dark:text-blue-400 hover:text-[#0d2742] dark:hover:text-blue-300 border border-[#103254]/30 dark:border-blue-400/30 rounded-lg px-2.5 py-1 hover:bg-[#103254]/5 dark:hover:bg-blue-400/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {loadingIA ? 'Generando…' : 'Generar con IA'}
                    </button>
                  ) : undefined
                }
              >
                <Textarea
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  rows={3}
                  placeholder={t('addProductDialog.descPlaceholder')}
                  className="resize-none dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </Field>

              {/* Precio */}
              <Field label={t('addProductDialog.priceLabel')}>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={precio}
                  onChange={(e) => setPrecio(e.target.value)}
                  placeholder="0.00"
                  className="h-10 dark:bg-white/5 dark:border-white/10 dark:text-white"
                />
              </Field>

              {/* Estado */}
              <div className="flex items-center justify-between gap-4 py-1">
                <div>
                  <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                    {t('addProductDialog.statusLabel')}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-0.5">
                    {t('addProductDialog.statusDesc')}
                  </p>
                </div>
                <Switch checked={activo} onCheckedChange={setActivo} />
              </div>
            </div>
          </div>

          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-row gap-2 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={closeDialog}
              disabled={saving}
              className={`flex-1 ${DLG_BTN_SECONDARY}`}
            >
              {t('addProductDialog.cancel')}
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !nombre.trim()}
              className={`flex-1 ${DLG_BTN_PRIMARY}`}
            >
              {saving
                ? t('addProductDialog.saving')
                : modoEdicion
                ? t('addProductDialog.update')
                : t('addProductDialog.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {fileToCrop && (
        <ProductImageCropDialog
          open={openCrop}
          file={fileToCrop}
          onCancel={() => {
            setOpenCrop(false);
            setFileToCrop(null);
          }}
          onSave={handleCroppedSave}
        />
      )}

      <GenerateDescriptionDialog
        open={showIADialog}
        onOpenChange={setShowIADialog}
        descripcion={iaDescripcion}
        loading={loadingIA}
        onAccept={() => {
          setDescripcion(iaDescripcion);
          setShowIADialog(false);
        }}
        onRegenerate={handleSiguienteVariante}
      />
    </>
  );
}